import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import React, {useContext, useState} from "react";
import TextField from "@mui/material/TextField";
import {useTheme} from "@mui/material/styles";
import * as yup from "yup";
import {useFormik} from "formik";
import {doc, updateDoc, arrayUnion, arrayRemove} from "firebase/firestore";
import {db} from "../../Firebase";
import {kRoomStateCollectionName} from "../../utils/CollectionConstants";
import EmailBadge from "./EmailBadge";
import {FetchContext} from "../../context/FetchContext";
import {AuthContext} from "../../context/AuthContext";
import mixpanel from "mixpanel-browser";
import {MiniTrashButton, SessionTrashButton} from "../../components/CoolButtons";
import {Button} from "@mui/material";


const CopyIcon = () => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none">
            <path
                d="M8.11873 4.73089L8.94414 5.5563C9.32336 5.93548 9.62417 6.38564 9.82941 6.88108C10.0346 7.37652 10.1403 7.90754 10.1403 8.4438C10.1403 8.98007 10.0346 9.51108 9.82941 10.0065C9.62417 10.502 9.32336 10.9521 8.94414 11.3313L8.73764 11.5372C7.97183 12.303 6.93317 12.7333 5.85014 12.7333C4.76712 12.7333 3.72846 12.303 2.96264 11.5372C2.19683 10.7714 1.7666 9.73274 1.7666 8.64972C1.7666 7.5667 2.19683 6.52803 2.96264 5.76222L3.78806 6.58764C3.51528 6.85804 3.29859 7.17966 3.15043 7.53404C3.00228 7.88841 2.92558 8.26856 2.92474 8.65266C2.9239 9.03675 2.99893 9.41723 3.14553 9.77225C3.29213 10.1273 3.50741 10.4498 3.77901 10.7214C4.05061 10.993 4.37317 11.2083 4.72819 11.3549C5.08321 11.5015 5.46369 11.5765 5.84779 11.5757C6.23189 11.5749 6.61203 11.4982 6.96641 11.35C7.32078 11.2019 7.6424 10.9852 7.91281 10.7124L8.11931 10.5059C8.6661 9.95893 8.97327 9.2172 8.97327 8.4438C8.97327 7.67041 8.6661 6.92868 8.11931 6.38172L7.29389 5.5563L8.11931 4.73147L8.11873 4.73089ZM12.0376 8.2373L11.2127 7.41247C11.4855 7.14206 11.7022 6.82044 11.8504 6.46607C11.9985 6.11169 12.0752 5.73155 12.076 5.34745C12.0769 4.96335 12.0019 4.58287 11.8553 4.22785C11.7087 3.87283 11.4934 3.55027 11.2218 3.27867C10.9502 3.00707 10.6276 2.79179 10.2726 2.64519C9.91757 2.4986 9.53709 2.42356 9.153 2.4244C8.7689 2.42524 8.38875 2.50194 8.03438 2.6501C7.68 2.79825 7.35838 3.01494 7.08798 3.28772L6.88148 3.49422C6.33469 4.04118 6.02752 4.78291 6.02752 5.5563C6.02752 6.3297 6.33469 7.07143 6.88148 7.61839L7.70689 8.4438L6.88148 9.26864L6.05664 8.4438C5.67743 8.06463 5.37661 7.61446 5.17138 7.11902C4.96615 6.62358 4.86052 6.09257 4.86052 5.5563C4.86052 5.02004 4.96615 4.48902 5.17138 3.99358C5.37661 3.49814 5.67743 3.04798 6.05664 2.6688L6.26314 2.46289C7.02896 1.69707 8.06762 1.26685 9.15064 1.26685C10.2337 1.26685 11.2723 1.69707 12.0381 2.46289C12.804 3.2287 13.2342 4.26736 13.2342 5.35039C13.2342 6.43341 12.804 7.47207 12.0381 8.23789L12.0376 8.2373Z"
                fill="white"/>
        </svg>
    )
}

// New side panel design
export const NewInviteParticipantCard = ({ onClose, inviteLink, invitedEmails = [], roomName }) => {
    const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [emailAlreadyInvited, setEmailAlreadyInvited] = useState('');
    const [inviteSuccess, setInviteSuccess] = useState(null);

    const fetchContext = useContext(FetchContext);
    const authContext = useContext(AuthContext);
    const theme = useTheme();

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(inviteLink);
            setInviteLinkCopied(true);
            setTimeout(() => setInviteLinkCopied(false), 2000);
        } catch {
            // Fallback: select text in the input if clipboard fails (handled by readOnly input focus below)
            setInviteLinkCopied(false);
        }
    };

    const copyButtonText = inviteLinkCopied ? 'Copied' : 'Copy link';

    // ---- styles (unchanged except small tweaks) ----
    const sidePanelStyle = {
        position: 'fixed', top: 0, right: 0, width: 420, height: '100vh',
        bgcolor: '#fff', boxShadow: '-4px 0 20px rgba(0,0,0,.1)', zIndex: 1300,
        overflowY: 'auto', display: 'flex', flexDirection: 'column', borderLeft: '1px solid #e8e8e8'
    };
    const headerStyle = {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        p: '24px', borderBottom: '1px solid #f0f0f0', bgcolor: '#fafafa'
    };
    const titleStyle = { fontFamily: 'Inter', fontSize: 22, fontWeight: 600, color: '#1a1a1a', lineHeight: '28px' };
    const closeButtonStyle = {
        cursor: 'pointer', p: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        '&:hover': { backgroundColor: '#f0f0f0' }
    };
    const contentStyle = { p: '24px', flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' };
    const sectionStyle = { display: 'flex', flexDirection: 'column', gap: '12px' };
    const sectionTitleStyle = { fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#1a1a1a' };
    const sectionDescriptionStyle = { fontFamily: 'Inter', fontSize: 14, color: '#6f6e77', lineHeight: '20px' };
    const inputFieldStyle = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#f8f9fa', borderRadius: '8px',
            '&:hover': { backgroundColor: '#f0f1f2' },
            '&.Mui-focused': { backgroundColor: '#fff', boxShadow: '0 0 0 2px rgba(43,107,253,.1)' }
        }
    };
    const dividerStyle = { display: 'flex', alignItems: 'center', my: '8px', gap: '12px', color: '#6f6e77' };

    // ---- validation (kept simple) ----
    const validationSchema = yup.object({
        email: yup.string().trim().email('Enter a valid email').required('Email is required.')
    });
    const formik = useFormik({ initialValues: { email: '' }, validationSchema });

    // ---- data ops ----
    const saveInviteEmail = async (email) => {
        const entityRef = doc(db, kRoomStateCollectionName, roomName);
        await updateDoc(entityRef, { invitedEmails: arrayUnion(email) });
    };
    const removeInvitedEmail = async (email) => {
        const entityRef = doc(db, kRoomStateCollectionName, roomName);
        await updateDoc(entityRef, { invitedEmails: arrayRemove(email) });
    };
    const sendInviteEmail = async (email) => {
        await fetchContext.authAxios.post(
            `/invite`,
            { recipientEmail: email, eventTime: Math.floor(Date.now() / 1000) },
            { headers: { Authorization: `Bearer ${authContext.getToken()}` } }
        );
    };

    const handleSendInvite = async () => {
        setInviteSuccess(null);
        const raw = formik.values.email || '';
        const email = raw.trim().toLowerCase();

        // trigger validation messages
        formik.setFieldTouched('email', true, true);
        await formik.validateForm();

        if (!email || formik.errors.email) {
            setEmailError(true);
            setEmailAlreadyInvited('');
            return;
        }
        setEmailError(false);

        if (invitedEmails.includes(email)) {
            setEmailAlreadyInvited(email);
            return;
        }
        setEmailAlreadyInvited('');

        setIsSending(true);
        try {
            mixpanel.track('Invited_Sent_From_Studio', { roomName });
            await Promise.all([saveInviteEmail(email), sendInviteEmail(email)]);
            setInviteSuccess(`Invite sent to ${email}.`);
            formik.resetForm();
        } catch (err) {
            setInviteSuccess('Could not send invite. Please try again.');
            // console.error(err);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Box sx={sidePanelStyle} role="dialog" aria-labelledby="invite-title" aria-modal="true">
            {/* Header */}
            <Box sx={headerStyle}>
                <Typography id="invite-title" sx={titleStyle}>Invite participants</Typography>
                <Box sx={closeButtonStyle} onClick={onClose} aria-label="Close invite panel">
                    <CloseIcon sx={{ fontSize: 20, color: '#6f6e77' }} />
                </Box>
            </Box>

            {/* Content */}
            <Box sx={contentStyle}>
                {/* Share Link */}
                <Box sx={sectionStyle}>
                    <Typography sx={sectionTitleStyle}>Share a link</Typography>
                    <Typography sx={sectionDescriptionStyle} id="share-link-help">
                        Copy this link to invite people to your studio.
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <TextField
                            aria-describedby="share-link-help copy-feedback"
                            variant="outlined"
                            value={inviteLink}
                            onFocus={(e) => e.currentTarget.select()}
                            InputProps={{ readOnly: true, inputProps: { 'aria-readonly': true } }}
                            sx={inputFieldStyle}
                            fullWidth
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={copyToClipboard}
                            disabled={inviteLinkCopied}
                            aria-live="polite"
                            sx={{ textTransform: 'none', py: 1.25, borderRadius: '8px' }}
                        >
                            <CopyIcon style={{ marginRight: 8 }} />
                            {copyButtonText}
                        </Button>
                        <Typography id="copy-feedback" sx={{ fontSize: 12, color: '#6f6e77', minHeight: 16 }} aria-live="polite">
                            {inviteLinkCopied ? 'Link copied to clipboard.' : ''}
                        </Typography>
                    </Box>
                </Box>

                {/* Divider */}
                <Box sx={dividerStyle}>
                    <Box sx={{ flex: 1, height: "1px", bgcolor: '#e8e8e8' }} />
                    <Typography sx={{ fontSize: 12 }}>or</Typography>
                    <Box sx={{ flex: 1, height: "1px", bgcolor: '#e8e8e8' }} />
                </Box>

                {/* Invite via email */}
                <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSendInvite(); }} sx={sectionStyle} noValidate>
                    <Typography sx={sectionTitleStyle}>Invite via email</Typography>
                    <Typography sx={sectionDescriptionStyle} id="email-help">
                        We’ll email instructions to join your studio.
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <TextField
                            variant="outlined"
                            name="email"
                            placeholder="Enter email address"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={Boolean(formik.touched.email && formik.errors.email)}
                            helperText={formik.touched.email && formik.errors.email}
                            aria-describedby="email-help invite-feedback"
                            sx={inputFieldStyle}
                            fullWidth
                            autoComplete="email"
                            inputMode="email"
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSending || !formik.values.email.trim()}
                            sx={{ textTransform: 'none', py: 1.25, borderRadius: '8px' }}
                        >
                            {isSending ? 'Sending…' : 'Send invite'}
                        </Button>
                    </Box>

                    {/* Inline feedback (a11y-friendly) */}
                    <Box id="invite-feedback" aria-live="polite" sx={{ minHeight: 18 }}>
                        {emailError && (
                            <Typography variant="body2" color="error" sx={{ fontSize: 13 }}>
                                Please enter a valid email address.
                            </Typography>
                        )}
                        {emailAlreadyInvited && (
                            <Typography variant="body2" color="error" sx={{ fontSize: 13 }}>
                                You’ve already invited {emailAlreadyInvited}.
                            </Typography>
                        )}
                        {inviteSuccess && !emailError && !emailAlreadyInvited && (
                            <Typography variant="body2" sx={{ fontSize: 13, color: '#1a7f37' }}>
                                {inviteSuccess}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Invited list */}
                {invitedEmails.length > 0 && (
                    <Box sx={sectionStyle}>
                        <Typography sx={sectionTitleStyle}>Invited participants ({invitedEmails.length})</Typography>
                        <Typography sx={sectionDescriptionStyle}>People you’ve invited to join your studio.</Typography>

                        <Box sx={{
                            display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: 300, overflowY: 'auto',
                            p: '12px', bgcolor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e8e8e8'
                        }}>
                            {invitedEmails.map((email, index) => (
                                <Box key={index} sx={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    p: '10px 12px', bgcolor: '#fff', borderRadius: '6px', border: '1px solid #f0f0f0',
                                    '&:hover': { borderColor: '#e0e0e0' }
                                }}>
                                    <EmailBadge email={email} />
                                    <SessionTrashButton
                                        aria-label={`Remove ${email}`}
                                        handleClick={() => removeInvitedEmail(email)}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

